// src/pages/activity/ActivitySettingsPage.tsx
// Tabbed: personal inbox preferences (everyone), event-source mappings (admin).
// See docs/ux/activity-ia.md section 4.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { usePlatformRole } from '@/hooks/usePlatformRole';
import { PreferencesTab } from '@/features/activity/PreferencesTab';
import { MappingsTab } from '@/features/activity/MappingsTab';

type Tab = 'preferences' | 'mappings';

export const ActivitySettingsPage = observer(() => {
  const { t } = useI18n();
  const { isAdmin } = usePlatformRole();
  const [tab, setTab] = useState<Tab>('preferences');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'preferences', label: t('activity.settings.tabs.preferences') },
    ...(isAdmin ? [{ id: 'mappings' as Tab, label: t('activity.settings.tabs.mappings') }] : []),
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <nav className="mb-2 text-sm">
          <Link to="/activity" className="text-muted-foreground hover:text-foreground">{t('activity.page.title')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('activity.settings.title')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('activity.settings.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('activity.settings.subtitle')}</p>
      </div>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === tb.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'preferences' && <PreferencesTab />}
        {tab === 'mappings' && isAdmin && <MappingsTab />}
      </div>
    </div>
  );
});

export default ActivitySettingsPage;
